import { useMutation, useQueryClient, UseMutationResult } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { authService } from "@/services/auth-service";
import { loginStart, loginSuccess, loginFailure } from "@/redux/features/auth-slice";
import { setUser } from "@/redux/features/user-slice";
import { fetchRoles } from "@/redux/features/roles-slice";
import { AppDispatch } from "@/redux/store";

type LoginCredentials = {
  email: string;
  password: string;
};

type LoginResult = {
  message?: string;
  user?: User | null;
};

/** Posts to this app's own /api/auth/login route (sets httpOnly cookies server-side), then syncs the returned user into both the React Query cache and the existing Redux user/roles/auth slices. */
export function useLogin(): UseMutationResult<LoginResult, unknown, LoginCredentials> {
  const queryClient = useQueryClient();
  const dispatch = useDispatch<AppDispatch>();

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      dispatch(loginStart());
      const response = await authService.login(credentials);
      return response.data;
    },
    onSuccess: (data) => {
      if (!data.user) return;
      queryClient.setQueryData(["auth", "session"], data.user);
      dispatch(setUser(data.user));
      dispatch(loginSuccess());
      dispatch(fetchRoles());
    },
    onError: (error) => {
      dispatch(loginFailure(error instanceof Error ? error.message : "Could not sign in."));
    },
  });
}
